import * as yup from "yup"
import { Selection } from "../enums"
import { nameof } from "ts-simple-nameof"
import { VehicleIdentificationDto, VehiclePriceDto } from "../dtos"
import { customNullable } from "."

export const znackaRequiredTypes = [
  Selection.Personal,
  Selection.Utility,
  //Selection.Motorcycle,
  Selection.Offroad,
]

const carUpTo3500kgTypes = [...znackaRequiredTypes]

export const isOtherMake = (make?: string | null) => {
  return (make ?? '').toUpperCase() == 'OSTATNÍ'
}

export const IsTrailer = (category?: string | null) =>
  category &&
  [Selection.Trailer, Selection.SemiTrailer].includes(parseInt(category))

export const IsTruck = (category?: string | null) =>
  category && [Selection.TruckOver35t].includes(parseInt(category))

export const IsTractor = (category?: string | null) =>
  category &&
  [
    Selection.TractorWithRegistrationMark,
    Selection.TractorWithoutRegistrationMark,
  ].includes(parseInt(category))

export const IsTahac = (category?: string | null) =>
  category && [Selection.Tahac].includes(parseInt(category))

export const IsWorkMachine = (category?: string | null) =>
  category &&
  [Selection.WorkMachine, Selection.WorkMachineNoRegistration].includes(
    parseInt(category)
  )

export const IsCamperVehicle = (category?: string | null) =>
  category && [Selection.CamperVehicle].includes(parseInt(category))

export const IsOtherNonregistered = (category?: string | null) =>
  category && [Selection.OtherNonregistered].includes(parseInt(category))

export const IsVolumeFuel = (fuel?: string | null) =>
  !fuel || ![Selection.Electricity].includes(parseInt(fuel))

export const IsSubaruLessThan3YearsOld = (make?: string | null, firstRegDate?: Date | null) => {
  if (make && make.toUpperCase() == 'SUBARU' && firstRegDate) {
    const regDate = (typeof (firstRegDate) === 'string') ? new Date(firstRegDate) : firstRegDate
    const date = new Date()
    date.setFullYear(date.getFullYear() - 3)
    return regDate >= date
  }
  return false
}

export const VehicleValidationRuleObject = {
  licensePlate: yup
    .string()
    .nullable()
    .max(8, "SPZ může mít maximálně 8 znaků")
    .when("licensePlate", {
      is: (spz?: string) => (spz?.length ?? 0) > 0,
      then: () => yup
        .string()
        .matches(/^[aA-zZ0-9\s]+$/, "SPZ nesmí obsahovat nepovolené znaky"),
      otherwise: undefined,
    }),

  vin: yup
    .string()
    .nullable()
    .when(
      [
        nameof<VehicleIdentificationDto>((x) => x.vin),
        nameof<VehicleIdentificationDto>((x) => x.licensePlate),
        nameof<VehiclePriceDto>((x) => x.useVehicleEvaluation),
      ],
      {
        is: (
          vin?: string,
          licensePlate?: string,
          useVehicleEvaluation?: boolean
        ) =>
          (vin?.length ?? 0) == 0 &&
          (licensePlate?.length ?? 0) == 0 &&
          useVehicleEvaluation == true,
        then: () => yup
          .string()
          .required(
            "Jedno z polí VIN nebo Reg. značka (SPZ) musí být vyplněno"
          ),
        otherwise: undefined,
      }
    )
    .when(["vin", "druh"], {
      is: (vin?: string, druh?: number) => (vin?.length ?? 0) > 0 && !IsOtherNonregistered(druh?.toString()),
      then: () => yup
        .string()
        .matches(/^[aA-zZ0-9\s]+$/, "VIN nesmí obsahovat nepovolené znaky"),
      otherwise: undefined,
    }),

  druh: yup.number().required("Vyberte druh vozidla"),

  pocetMistSezeni: customNullable(yup.number())
    .when("druh", {
      is: (druh?: number) => !IsTrailer(druh?.toString()) && !IsOtherNonregistered(druh?.toString()),
      then: () => customNullable(yup.number()).required("Zadejte počet míst k sezení"),
      otherwise: undefined,
    }),

  palivo: customNullable(yup.number())
    .when("druh", {
      is: (druh?: number) => !IsTrailer(druh?.toString()),
      then: () => customNullable(yup.number()).required("Vyberte palivo"),
      otherwise: undefined,
    }),

  znacka: yup
    .string()
    .nullable()
    .when("druh", {
      is: (druh?: number) =>
        druh && znackaRequiredTypes.includes(parseInt(druh.toString())),
      then: () => yup
        .string()
        .matches(/^[aA-zZ0-9čČšŠěĚëË\s\-\&]+$/, "Vyberte značku vozidla"),
      otherwise: undefined,
    }),

  znackaText: yup
    .string()
    .nullable()
    .when(["znacka", "druh"], {
      is: (znacka?: string, druh?: number) =>
        !znacka &&
        druh &&
        !znackaRequiredTypes.includes(parseInt(druh.toString())),
      then: () => yup
        .string()
        .matches(/^[aA-zZ0-9čČšŠěĚëË\s\-\&]+$/, "Zadejte značku vozidla"),
      otherwise: undefined,
    }),

  model: yup.string().required("Zadejte model"),

  objem: customNullable(yup.number())
    .when(["druh", "palivo"], {
      is: (druh?: number, palivo?: number) =>
        !IsTrailer(druh?.toString()) && !IsOtherNonregistered(druh?.toString()) && IsVolumeFuel(palivo?.toString()),
      then: () => customNullable(yup.number()).required("Zadejte objem"),
      otherwise: undefined,
    }),

  vykon1: customNullable(yup.number()).when("druh", {
    is: (druh?: number) => !IsTrailer(druh?.toString()),
    then: () => customNullable(yup.number())
      .min(1, 'Hodnota výkonu musí být nejméně 1 kW')
      .required("Zadejte výkon"),
    otherwise: undefined,
  }),

  celkovaHmotnost: customNullable(yup.number())
    .when("druh", {
      is: (druh?: number | null) =>
        !IsTrailer(druh?.toString()) &&
        !IsTruck(druh?.toString()) &&
        !IsTractor(druh?.toString()) &&
        !IsTahac(druh?.toString()) &&
        !IsWorkMachine(druh?.toString()) &&
        !IsCamperVehicle(druh?.toString()),
      then: () => customNullable(yup.number())
        .required("Zadejte celkovou hmotnost")
        .max(3500, "Celková hmotnost vozidla musí být maximálně 3 500 kg"),
      otherwise: () => customNullable(yup.number())
        .required("Zadejte celkovou hmotnost"),
    }),

  usage: yup.string().required("Vyberte způsob použití"),

  firstRegistrationDate: yup
    .string()
    .required("Pole datum první registrace je povinné"),

  mileage: customNullable(yup.number())
    .transform((currentValue: any, originalValue: any) => {
      return originalValue === "" ? null : currentValue
    })
    .when(["druh", "doNotValidateMileage"], {
      is: (druh?: number, doNotValidateMileage?: boolean) =>
        !(IsTrailer(druh?.toString()) || IsOtherNonregistered(druh?.toString())) && !doNotValidateMileage,
      then: () => customNullable(yup.number())
        .min(0, 'Hodnota nesmí být záporná')
        .max(99999999, 'Překročena maximální hodnota')
        .required(
          "Pole stav tachometru je povinné"
        ),
      otherwise: undefined,
    }),

  warrantyKmLimit: customNullable(yup.string())
    .when(
      "manufacturerWarrantyType",
      {
        is: (manufacturerWarrantyType?: number) =>
          manufacturerWarrantyType == 1,
        then: () => customNullable(yup.string()).required("Vyberte počet km záruky"),
        otherwise: undefined,
      }
    ),

  warrantyTermLimit: customNullable(yup.string()).when(
    "manufacturerWarrantyType",
    {
      is: (manufacturerWarrantyType?: number) =>
        manufacturerWarrantyType == 1,
      then: () => customNullable(yup.string()).required(
        "Vyberte počet měsíců záruky"
      ),
      otherwise: undefined,
    }
  ),

  extraEquipments: yup.array().of(
    yup.object().shape({
      name: yup.string().required("Zadejte popis mimořádné výbavy"),
      price: customNullable(yup.number()).required(
        "Zadejte hodnotu mimořádné výbavy"
      ),
      /*purchaseDate: customNullable(yup.string()).required( //Pro implementaci u Kooperativy datum pořízení není potřeba
        "Zadejte datum pořízení mimořádné výbavy"
      ),*/
    })
  )
  //  ,

  //yearKm: customNullable(yup.string())
  //  .when("druh", {
  //    is: (druh?: number) => {
  //      console.log('Vyberte roční nájezd km', druh && carUpTo3500kgTypes.includes(parseInt(druh.toString())))
  //      return druh && carUpTo3500kgTypes.includes(parseInt(druh.toString()))
  //    },
  //    then: () => customNullable(yup.string()).required("Vyberte roční nájezd km"),
  //    otherwise: undefined,
  //  }),

}

export const AddVehicleInfoValidationRuleObject = {
  firstRegistrationDate: customNullable(yup.string()
    .when(["priceType", 'useVehicleEvaluation'], {
      is: (priceType?: number, useVehicleEvaluation?: boolean) => priceType == Selection.VehicleValuation && useVehicleEvaluation == true,
      then: () =>
        yup.string()
          .required("Pole datum první registrace je povinné"),
      otherwise: () => yup.string(),
    })),

  mileage: customNullable(yup.number())
    .transform((currentValue: any, originalValue: any) => {
      return originalValue === "" ? null : currentValue
    })
    .when(["druh", "priceType", 'useVehicleEvaluation'], {
      is: (druh?: number, priceType?: number, useVehicleEvaluation?: boolean) => !(IsTrailer(druh?.toString()) || IsOtherNonregistered(druh?.toString())) && priceType == Selection.VehicleValuation && useVehicleEvaluation == true,
      then: () => customNullable(yup.number())
        .min(0, 'Hodnota nesmí být záporná')
        .max(99999999, 'Překročena maximální hodnota')
        .required(
          "Pole stav tachometru je povinné"
        ),
      otherwise: undefined,
    })
}

export const DenniPovVehicleValidationRuleObject = {
  licensePlate: yup
    .string()
    .nullable()
    .max(8, "SPZ může mít maximálně 8 znaků")
    .when("licensePlate", {
      is: (spz?: string) => (spz?.length ?? 0) > 0,
      then: () => yup
        .string()
        .matches(/^[aA-zZ0-9\s]+$/, "SPZ nesmí obsahovat nepovolené znaky"),
      otherwise: undefined,
    }),

  vin: yup
    .string()
    .when(
      [
        nameof<VehicleIdentificationDto>((x) => x.vin),
        nameof<VehicleIdentificationDto>((x) => x.licensePlate),
        nameof<VehiclePriceDto>((x) => x.useVehicleEvaluation),
      ],
      {
        is: (
          vin?: string,
          licensePlate?: string,
          useVehicleEvaluation?: boolean
        ) =>
          (vin?.length ?? 0) == 0 &&
          (licensePlate?.length ?? 0) == 0 &&
          useVehicleEvaluation == true,
        then: () => yup
          .string()
          .required(
            "Jedno z polí VIN nebo Reg. značka (SPZ) musí být vyplněno"
          ),
        otherwise: undefined,
      }
    )
    .when(["vin", "druh"], {
      is: (vin?: string, druh?: number) => (vin?.length ?? 0) > 0 && !IsOtherNonregistered(druh?.toString()),
      then: () => yup
        .string()
        .matches(/^[aA-zZ0-9\s]+$/, "VIN nesmí obsahovat nepovolené znaky"),
      otherwise: undefined,
    }),

  druh: yup.number().required("Vyberte druh vozidla"),

  palivo: customNullable(yup.number())
    .when("druh", {
      is: (druh?: number) => !IsTrailer(druh?.toString()),
      then: () => customNullable(yup.number()).required("Vyberte palivo"),
      otherwise: undefined,
    }),

  znacka: yup
    .string()
    .nullable()
    .when("druh", {
      is: (druh?: number) =>
        druh && znackaRequiredTypes.includes(parseInt(druh.toString())),
      then: () => yup
        .string()
        .matches(/^[aA-zZ0-9čČšŠěĚëË\s\-\&]+$/, "Vyberte značku vozidla"),
      otherwise: undefined,
    }),
  model: yup.string().required("Zadejte model"),

  objem: customNullable(yup.number()).when(["druh", "palivo"], {
    is: (druh?: number, palivo?: number) =>
      !(IsTrailer(druh?.toString()) || IsOtherNonregistered(druh?.toString())) && IsVolumeFuel(palivo?.toString()),
    then: () => customNullable(yup.number()).required("Zadejte objem"),
    otherwise: undefined,
  }),

  vykon1: customNullable(yup.number()).when("druh", {
    is: (druh?: number) => !IsTrailer(druh?.toString()),
    then: () => customNullable(yup.number()).required("Zadejte výkon"),
    otherwise: undefined,
  }),

  celkovaHmotnost: customNullable(yup.number())
    .when("druh", {
      is: (druh?: number | null) =>
        !IsTrailer(druh?.toString()) &&
        !IsTruck(druh?.toString()) &&
        !IsTractor(druh?.toString()) &&
        !IsTahac(druh?.toString()) &&
        !IsWorkMachine(druh?.toString()) &&
        !IsCamperVehicle(druh?.toString()),
      then: () => customNullable(yup.number())
        .required("Zadejte celkovou hmotnost")
        .max(3500, "Celková hmotnost vozidla musí být maximálně 3 500 kg"),
      otherwise: () => customNullable(yup.number())
        .required("Zadejte celkovou hmotnost"),
    }),

  firstRegistrationDate: yup
    .string()
    .required("Pole datum první registrace je povinné"),

  mileage: customNullable(yup.number())
    .transform((currentValue: any, originalValue: any) => {
      return originalValue === "" ? null : currentValue
    })
    .when(["druh", "doNotValidateMileage"], {
      is: (druh?: number, doNotValidateMileage?: boolean) =>
        !(IsTrailer(druh?.toString()) || IsOtherNonregistered(druh?.toString())) && !doNotValidateMileage,
      then: () => customNullable(yup.number())
        .min(0, 'Hodnota nesmí být záporná')
        .max(99999999, 'Překročena maximální hodnota')
        .required(
          "Pole stav tachometru je povinné"
        ),
      otherwise: undefined,
    }),
}


// zalezi na poradi - v tomto poradi se pak mezi errory preklikava
export const VehicleValidationSchema = yup.object().shape(
  VehicleValidationRuleObject,
  [
    // osetreni cyclic dependency vznikajici pri pouziti when... is... then...
    ["vin", "vin"],
    ["licensePlate", "licensePlate"]
  ]
) as any

// zalezi na poradi - v tomto poradi se pak mezi errory preklikava
export const DenniPovVehicleValidationSchema = yup.object().shape(
  DenniPovVehicleValidationRuleObject,
  [
    ["vin", "vin"],
    ["licensePlate", "licensePlate"]
  ]
) as any

